import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { fadeOut } from '@freelancer/animations';
import { ContainerComponent, ContainerSize } from '@freelancer/ui/container';
import { IconColor, IconModule, IconSize, IconName } from '@freelancer/ui/icon';
import {
  PictureBackgroundImageStyles,
  PictureBgImageObjectFit,
  PictureBgImagePosition,
  PictureComponent,
} from '../picture';
import { BannerAnnouncementButtonsComponent } from './banner-announcement-buttons.component';
import { BannerAnnouncementType } from './banner-announcement.types';

@Component({
  standalone: true,
  imports: [ContainerComponent, IconModule, PictureComponent],
  selector: 'fl-banner-announcement',
  template: `
    <div
      class="AnnouncementBackground"
      role="alert"
      [attr.data-type]="type"
      [@hideElement]="state"
      (@hideElement.done)="animationDone($event)"
    >
      <fl-container
        class="AnnouncementContentContainer"
        [class.AnnouncementImageBanner]="isImageBanner"
        [size]="size"
        [mobilePadding]="true"
      >
        @if (isImageBanner && imageSrc) {
          <fl-picture
            [backgroundStyles]="backgroundImageStyles"
            [isBackgroundImage]="true"
            [srcDesktop]="imageDesktopSrc"
            [srcTablet]="imageTabletSrc"
            [src]="imageSrc"
          ></fl-picture>
        }
        <div class="mr-xsmall">
          <fl-icon
            [name]="icon"
            [color]="IconColor.LIGHT"
          ></fl-icon>
        </div>
        <div class="AnnouncementWrap">
          <div class="AnnouncementContent mr-large">
            @if (bannerTitle) {
              <p class="text-light text-body-16 font-bold mb-none">
                {{ bannerTitle }}
              </p>
            }
            <div
              class="AnnouncementContent-body text-body-16 text-light font-normal tablet:mb-none {{
                injectedButtons ? 'mb-xsmall' : 'mb-none'
              }}"
            >
              <!-- only inject subcomponents if they're necessary for buttons -->
              <!-- this should make the interface nicer for non-button banners -->
              @if (injectedButtons) {
                <ng-content
                  select="fl-banner-announcement-message"
                ></ng-content>
              } @else {
                <ng-content></ng-content>
              }
            </div>
          </div>
          @if (injectedButtons) {
            <div class="AnnouncementButtons mr-xsmall">
              <ng-content select="fl-banner-announcement-buttons"></ng-content>
            </div>
          }
        </div>

        @if (closeable) {
          <fl-icon
            class="AlertCloseButton"
            i18n-label="Banner announcement close icon label"
            label="Close banner announcement"
            [name]="'ui-close'"
            [color]="IconColor.LIGHT"
            [useIconFont]="true"
            (click)="handleCloseEvent($event)"
          ></fl-icon>
        }
      </fl-container>
    </div>
  `,
  styleUrls: ['./banner-announcement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hideElement', [
      transition('visible => hidden', [
        useAnimation(fadeOut, {
          params: { duration: '500' },
        }),
      ]),
    ]),
  ],
})
export class BannerAnnouncementComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  state = 'visible';
  size: ContainerSize;

  @HostBinding('class.IsHidden') bannerAlertHidden = false;
  @Input() type = BannerAnnouncementType.INFO;
  @Input() bannerTitle?: string;
  @Input() closeable = true;
  @Input() icon: IconName;
  @Input() isImageBanner = false;
  /** Sets the backgroundStyles to be used when isImageBanner is true */
  @Input() backgroundImageStyles?: PictureBackgroundImageStyles = {
    position: PictureBgImagePosition.CENTER,
    objectFit: PictureBgImageObjectFit.COVER,
  };
  /** Sets the src image for desktop screen sizes when isImageBanner is true */
  @Input() imageDesktopSrc?: string;
  /** Sets the src image for tablet screen sizes when isImageBanner is true */
  @Input() imageTabletSrc?: string;
  /** Sets the src image for mobile screen sizes above when isImageBanner is true */
  @Input() imageSrc?: string;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  @Output() close = new EventEmitter<void>();

  @ContentChild(BannerAnnouncementButtonsComponent)
  injectedButtons: BannerAnnouncementButtonsComponent;

  animationDone(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.bannerAlertHidden = true;
    }
  }

  handleCloseEvent(event: MouseEvent): void {
    // Stops event bubble up to parent elements (i.e. link components to redirect)
    event.preventDefault();
    event.stopPropagation();

    this.state = 'hidden';
    this.close.emit();
  }
}
